import type { Editor } from '@tiptap/react';

import { Box, Button, Flex } from '@chakra-ui/react';
import { useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useMemo } from 'react';

import { createMarkdownHighlightCommands } from './markdown-highlight-menu';

interface MarkdownHighlightMenuPopoverProps {
  editor: Editor | null;
}

const MARKDOWN_HIGHLIGHT_MENU_BUTTON_SIZE = 44;
const MARKDOWN_HIGHLIGHT_MENU_BUTTON_GAP = 8;
const MARKDOWN_HIGHLIGHT_MENU_PADDING = 16;

export function MarkdownHighlightMenuPopover({
  editor,
}: MarkdownHighlightMenuPopoverProps) {
  const highlightMenuState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      if (!currentEditor) {
        return {
          active: false,
          bold: false,
          italic: false,
          strike: false,
          code: false,
        };
      }

      return {
        active:
          !currentEditor.state.selection.empty &&
          currentEditor.isEditable &&
          !currentEditor.isActive('codeBlock'),
        bold: currentEditor.isActive('bold'),
        italic: currentEditor.isActive('italic'),
        strike: currentEditor.isActive('strike'),
        code: currentEditor.isActive('code'),
      };
    },
  }) ?? {
    active: false,
    bold: false,
    italic: false,
    strike: false,
    code: false,
  };

  const commands = useMemo(
    () => (editor ? createMarkdownHighlightCommands(editor) : []),
    [editor],
  );

  const menuWidth =
    commands.length * MARKDOWN_HIGHLIGHT_MENU_BUTTON_SIZE +
    Math.max(0, commands.length - 1) * MARKDOWN_HIGHLIGHT_MENU_BUTTON_GAP +
    MARKDOWN_HIGHLIGHT_MENU_PADDING;

  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ state }) =>
        !state.selection.empty && !editor.isActive('codeBlock')
      }
      options={{
        placement: 'top',
        offset: 8,
      }}
    >
      <Box
        className="markdown-editor-highlight-menu"
        role="toolbar"
        aria-label="Highlight toolbar"
        minW={`${menuWidth}px`}
      >
        <Flex className="markdown-editor-highlight-menu__toolbar" wrap="wrap">
          {commands.map((command) => {
            const Icon = command.icon;
            const active =
              (command.id === 'bold' && highlightMenuState.bold) ||
              (command.id === 'italic' && highlightMenuState.italic) ||
              (command.id === 'strike' && highlightMenuState.strike) ||
              (command.id === 'inline-code' && highlightMenuState.code);

            return (
              <Button
                key={command.id}
                className="markdown-editor-highlight-menu__button"
                data-active={active ? 'true' : 'false'}
                variant="ghost"
                w="11"
                h="11"
                p="0"
                justifyContent="center"
                alignItems="center"
                fontWeight="normal"
                title={command.label}
                aria-label={command.label}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={command.run}
              >
                <Icon aria-hidden size={18} />
              </Button>
            );
          })}
        </Flex>
      </Box>
    </BubbleMenu>
  );
}
